import { useCallback, useEffect, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { useForm } from 'react-hook-form';
import type { FieldErrors } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { useTranslation } from '@/i18n';
import { useUI } from '@/hooks/ui/useUI';
import { fractionalLeaveService } from '@/api/domains/fractionalLeave/fractionalLeave.service';
import type { FractionalLeave } from '@/api/domains/fractionalLeave/fractionalLeave.types';
import { FRACTIONAL_LEAVE_LIST_REFRESH_EVENT } from '@/api/domains/fractionalLeave/fractionalLeave.types';
import { injectValidationErrors } from '@/utils/formValidationErrors';
import { normalizeHoursForBackend } from '@/utils';
import {
  editFractionalLeaveFormSchema,
  type EditFractionalLeaveFormValues,
} from '@/schemas/fractionalLeave/editFractionalLeaveForm';


// ── Hook params ────────────────────────────────────────────────────────────

export interface UseEditFractionalLeaveFormParams {
  record?: FractionalLeave;
  readOnly?: boolean;
  onSubmit?: () => void;
}

// ── Return type interface ──────────────────────────────────────────────────

export interface UseEditFractionalLeaveFormResult {
  nameValue: string;
  descriptionValue: string;
  hoursValue: string;
  deductibleValue: boolean;
  handleNameChange: (value: string) => void;
  handleDescriptionChange: (value: string) => void;
  handleHoursChange: (value: string) => void;
  handleDeductibleChange: (checked: boolean) => void;
  handleSubmit: (event?: FormEvent<HTMLFormElement>) => void;
  effectiveReadOnly: boolean;
  isSubmitting: boolean;
  isReady: boolean;
  errors: FieldErrors<EditFractionalLeaveFormValues>;
  isSubmitted: boolean;
}

// ── Hook ───────────────────────────────────────────────────────────────────

export function useEditFractionalLeaveForm({
  record,
  readOnly,
  onSubmit,
}: UseEditFractionalLeaveFormParams): UseEditFractionalLeaveFormResult {
  const { t: translate } = useTranslation();
  const { showNotification, showApiError, closeSidePanel } = useUI();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const effectiveReadOnly = !!readOnly;

  // ── Local field state ──────────────────────────────────────────────────
  const [nameValue, setNameValue] = useState('');
  const [descriptionValue, setDescriptionValue] = useState('');
  const [hoursValue, setHoursValue] = useState('');
  const [deductibleValue, setDeductibleValue] = useState(false);

  // ── React Hook Form ────────────────────────────────────────────────────
  const {
    handleSubmit: rhfHandleSubmit,
    setValue,
    setError,
    register,
    unregister,
    formState,
  } = useForm<EditFractionalLeaveFormValues>({
    resolver: zodResolver(editFractionalLeaveFormSchema),
    mode: 'onSubmit',
    defaultValues: {
      name: '',
      description: '',
      hours: '',
      deductible: false,
    },
  });

  // ── Register fields ────────────────────────────────────────────────────
  useEffect(() => {
    register('name');
    register('description');
    register('hours');
    register('deductible');
    return () => {
      unregister('name');
      unregister('description');
      unregister('hours');
      unregister('deductible');
    };
  }, [register, unregister]);

  // ── Pre-fill from record ───────────────────────────────────────────────
  useEffect(() => {
    if (!record) return;
    setNameValue(record.name);
    setDescriptionValue(record.description);
    setHoursValue(record.hours);
    setDeductibleValue(record.deductible);
    setValue('name', record.name, { shouldValidate: false });
    setValue('description', record.description, { shouldValidate: false });
    setValue('hours', record.hours, { shouldValidate: false });
    setValue('deductible', record.deductible, { shouldValidate: false });
  }, [record, setValue]);

  // ── Sync local state → RHF form ──────────────────────────────────────────
  // Needed to re-establish form values after React StrictMode re-registration
  // (unregister → register resets values; state persists but form does not).
  useEffect(() => {
    setValue('name', nameValue, { shouldValidate: false });
    setValue('description', descriptionValue, { shouldValidate: false });
    setValue('hours', hoursValue, { shouldValidate: false });
    setValue('deductible', deductibleValue, { shouldValidate: false });
  }, [nameValue, descriptionValue, hoursValue, deductibleValue, setValue]);

  // ── isReady ────────────────────────────────────────────────────────────
  const isReady = useMemo(
    () => nameValue.trim().length > 0 && descriptionValue.trim().length > 0,
    [nameValue, descriptionValue],
  );

  // ── Field change handlers ──────────────────────────────────────────────
  const handleNameChange = useCallback((value: string) => {
    setNameValue(value);
    setValue('name', value, { shouldDirty: true });
  }, [setValue]);

  const handleDescriptionChange = useCallback((value: string) => {
    setDescriptionValue(value);
    setValue('description', value, { shouldDirty: true });
  }, [setValue]);

  const handleHoursChange = useCallback((value: string) => {
    setHoursValue(value);
    setValue('hours', value, { shouldDirty: true });
  }, [setValue]);

  const handleDeductibleChange = useCallback((checked: boolean) => {
    setDeductibleValue(checked);
    setValue('deductible', checked, { shouldDirty: true });
  }, [setValue]);

  // ── Submit handler ─────────────────────────────────────────────────────
  const onValid = useCallback(
    async (data: EditFractionalLeaveFormValues) => {
      if (effectiveReadOnly || !record) return;
      setIsSubmitting(true);

      try {
        const response = await fractionalLeaveService.update(record.id, {
          name: data.name,
          description: data.description,
          hours: normalizeHoursForBackend(data.hours),
          deductible: data.deductible,
          isActive: record.isActive,
        });

        if (response.success) {
          showNotification('success', translate('fractionalLeave:messages.updateSuccess'));
          closeSidePanel();
          window.dispatchEvent(new CustomEvent(FRACTIONAL_LEAVE_LIST_REFRESH_EVENT));
          onSubmit?.();
        } else if (response.error?.validationErrors && response.error.validationErrors.length > 0) {
          injectValidationErrors(response.error.validationErrors, { setError });
        } else {
          showApiError(response);
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      effectiveReadOnly, record, showNotification, showApiError,
      translate, closeSidePanel, setError, onSubmit,
    ],
  );

  const onInvalid = useCallback((): void => {
    // Intentionally empty — errors displayed via isSubmitted guard
  }, []);

  const handleSubmit = useCallback(
    (event?: FormEvent<HTMLFormElement>) => {
      if (event && typeof event.preventDefault === 'function') {
        event.preventDefault();
        event.stopPropagation();
      }
      void rhfHandleSubmit(onValid, onInvalid)(event as FormEvent<HTMLFormElement>);
    },
    [rhfHandleSubmit, onValid, onInvalid],
  );

  return {
    nameValue,
    descriptionValue,
    hoursValue,
    deductibleValue,
    handleNameChange,
    handleDescriptionChange,
    handleHoursChange,
    handleDeductibleChange,
    handleSubmit,
    effectiveReadOnly,
    isSubmitting,
    isReady,
    errors: formState.errors,
    isSubmitted: formState.isSubmitted,
  };
}
